import {
    WIDGET_ERROR_SIGNALS,
    WIDGET_PARENT_ORIGIN_PARAM,
    type WidgetErrorSignal as WidgetSentSignal,
} from '@luminovo/apps-types';
import { logToExternalErrorHandlers } from '@luminovo/commons';

import { AREA_TAG } from './areaTag';

/**
 * A platform failure Luminovo wants to hear about, named from a closed set.
 *
 * Closed because the destination is the product's own Sentry client, which runs
 * `sendDefaultPii: true` and replay with `maskAllText: false` — a fixed vocabulary
 * means no app-authored text can travel at all, so there is nothing to scrub. It
 * also bounds the volume: a signal set that cannot grow cannot produce a long tail.
 */
export type WidgetErrorSignal =
    | 'widget_handshake_timeout'
    | 'widget_frame_load_error'
    | 'widget_session_unrecoverable'
    | 'widget_parent_origin_missing'
    | 'widget_run_template_missing'
    | 'widget_location_unsendable'
    | 'button_run_unreachable'
    | 'button_session_unrecoverable'
    | WidgetSentSignal
    | 'widget_unknown_signal';

export interface WidgetSignalContext {
    appId: string;
    widgetId: string;
    slotId: string;
    appsOrigin: string;
    /** HTTP status a run answered with, where one arrived. */
    runStatus?: number;
    /** What was thrown, where the signal came from a caught failure. */
    cause?: unknown;
    /** The build that sent a `widget.error`; absent for a signal the host raised. */
    sdkVersion?: string;
}

const SIGNAL_DIAGNOSES: Record<WidgetErrorSignal, string> = {
    widget_handshake_timeout: 'the frame never sent widget.ready — a 404 bundle, a CSP block, or a crash before mount',
    widget_frame_load_error: 'the widget document itself failed to load',
    widget_session_unrecoverable: 'the apps host keeps minting sessions this frame cannot use',
    widget_parent_origin_missing: `the bundle URL carries no ${WIDGET_PARENT_ORIGIN_PARAM}, so the SDK cannot pin its parent`,
    widget_run_template_missing:
        'the assignment carries no workflow_run_url_template, so the SDK falls back to the v1 run path',
    widget_location_unsendable:
        'the host could not express its own location as a slot.location message, so the frame renders without one',
    button_run_unreachable: 'a button run did not complete for a platform reason rather than an authoring one',
    button_session_unrecoverable: 'a button run kept getting 401 after re-minting the apps session',
    widget_slot_token_rejected:
        'the widget refused the slot context, while this container believed the handshake succeeded',
    widget_workflow_platform_failure: 'a workflow call failed for a platform reason rather than an authoring one',
    widget_completion_platform_failure: 'a Completion failed for a platform reason rather than an authoring one',
    widget_sdk_internal_error: 'SDK code threw where nothing is meant to',
    widget_unknown_signal: 'a widget reported a signal this product release has never heard of',
};

/**
 * The signal a `widget.error` message reports as.
 *
 * A name off the wire, so it can be one a newer SDK invented. An unfamiliar one
 * lands under `widget_unknown_signal` rather than being dropped, which is what
 * makes a version skew visible instead of silent.
 */
export function signalFromWidget(signal: string): WidgetErrorSignal {
    return (WIDGET_ERROR_SIGNALS as readonly string[]).includes(signal)
        ? (signal as WidgetSentSignal)
        : 'widget_unknown_signal';
}

/**
 * Reports one platform failure to whichever Sentry client the host app initialised.
 *
 * Reached both by the container's own detection and by a `widget.error` the frame
 * sent. Either way the severity and grouping rules are decided here, on the
 * frontend release train, rather than in a bundle that may never be rebuilt.
 */
export function reportWidgetSignal(signal: WidgetErrorSignal, context: WidgetSignalContext): void {
    // `cause` rather than a tag: `beforeSend` walks the cause chain, and a click made
    // offline reaches here as a fetch rejection it already knows to drop.
    const error = new Error(`[apps] ${context.appId}:${context.widgetId} — ${SIGNAL_DIAGNOSES[signal]}`, {
        cause: context.cause,
    });
    error.name = 'WidgetSignal';

    logToExternalErrorHandlers(error, {
        level: 'error',
        tags: {
            ...AREA_TAG,
            signal,
            app_id: context.appId,
            widget_id: context.widgetId,
            slot_id: context.slotId,
            apps_origin: context.appsOrigin,
            ...(context.runStatus === undefined ? {} : { run_status: String(context.runStatus) }),
            ...(context.sdkVersion === undefined ? {} : { sdk_version: context.sdkVersion }),
        },
        // Without the viewer in it, so one broken widget is one issue rather than one
        // per person who loaded the page it sits on.
        fingerprint: [signal, context.appId, context.widgetId],
    });
}

/**
 * Whether the apps host baked the parent origin into the widget's document URL.
 *
 * Our bug wearing a user-error costume if it is missing: the SDK has no other way to
 * learn the origin that framed it, so the handshake never completes and the frame
 * looks to everyone like a widget that failed to render.
 */
export function hasParentOrigin(bundleUrl: string): boolean {
    try {
        return new URL(bundleUrl).searchParams.has(WIDGET_PARENT_ORIGIN_PARAM);
    } catch {
        return false;
    }
}
